import { ShareLocation } from "@mui/icons-material";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  RebuildRouteDropdownDefaultActionSystemDefaultReset,
  RebuildRouteDropdownDefaultRebuildHeading,
  RebuildRouteDropdownSkipCacheRow,
  RebuildRouteDropdownSubmitButton,
} from "@ryde/controls";
import {
  formatRebuildRouteSaveCacheButtonCopy,
  formatRebuildRouteSaveCacheButtonTooltip,
  IRebuildRouteDefaultActionOptions,
  RebuildRouteTypeEnum,
  resolveSkipCacheDefaultRebuildType,
  type DirectionsServiceEnum,
} from "@ryde/shared";

interface ICourseRebuildRouteDropdownDefaultActionProps {
  skipCache: boolean;
  isSkipCacheDisabled?: boolean;
  hasUserOwnedRoutingWaypoints?: boolean;
  isLoading?: boolean;
  lastUsedProvider?: DirectionsServiceEnum | null;
  systemDefaultProvider: DirectionsServiceEnum;
  onSkipCacheChange: (skipCache: boolean) => void;
  onRebuild: (
    rebuildType: RebuildRouteTypeEnum,
    options?: IRebuildRouteDefaultActionOptions,
  ) => void;
}

export const CourseRebuildRouteDropdownDefaultAction: FC<
  ICourseRebuildRouteDropdownDefaultActionProps
> = ({
  skipCache,
  isSkipCacheDisabled = false,
  hasUserOwnedRoutingWaypoints = false,
  isLoading,
  lastUsedProvider,
  systemDefaultProvider,
  onSkipCacheChange,
  onRebuild,
}) => {
  const { t } = useTranslation();

  const rebuildType = useMemo(
    () =>
      resolveSkipCacheDefaultRebuildType({
        skipCachedPaths: skipCache,
        preferSkipCacheWithoutUpdate: hasUserOwnedRoutingWaypoints,
      }),
    [hasUserOwnedRoutingWaypoints, skipCache],
  );

  const cacheTooltip = useMemo(() => {
    if (!skipCache) {
      return t("general.SKIP_CACHED_PATHS_TOOLTIP");
    }

    if (hasUserOwnedRoutingWaypoints) {
      return t("general.REBUILD_ROUTE_SKIP_CACHE_TOOLTIP");
    }

    return t("general.REBUILD_REFRESH_CACHE_TOOLTIP");
  }, [hasUserOwnedRoutingWaypoints, skipCache, t]);

  const fullRebuildButtonCopy = useMemo(
    () =>
      formatRebuildRouteSaveCacheButtonCopy({
        lastUsedProvider,
        systemDefaultProvider,
        skipCachedPaths: skipCache,
        t,
      }),
    [lastUsedProvider, skipCache, systemDefaultProvider, t],
  );

  const saveCacheButtonTooltip = useMemo(
    () =>
      formatRebuildRouteSaveCacheButtonTooltip({
        skipCachedPaths: skipCache,
        t,
      }),
    [skipCache, t],
  );

  return (
    <>
      <RebuildRouteDropdownDefaultRebuildHeading />

      <div className="rebuild-route-dropdown-plate rebuild-route-dropdown-plate--default">
        <RebuildRouteDropdownSkipCacheRow
          checked={skipCache}
          disabled={isSkipCacheDisabled}
          label={t("general.SKIP_CACHE")}
          tooltip={cacheTooltip}
          onChange={onSkipCacheChange}
        />

        <RebuildRouteDropdownSubmitButton
          label={fullRebuildButtonCopy.title}
          hint={fullRebuildButtonCopy.hint}
          tooltip={saveCacheButtonTooltip}
          startIcon={<ShareLocation />}
          isLoading={isLoading}
          isSecondary
          isSkipCacheActive={skipCache}
          onClick={() => onRebuild(rebuildType)}
        />

        <RebuildRouteDropdownDefaultActionSystemDefaultReset
          lastUsedProvider={lastUsedProvider}
          systemDefaultProvider={systemDefaultProvider}
          isLoading={isLoading}
          onResetToSystemDefault={() =>
            onRebuild(RebuildRouteTypeEnum.Default, {
              provider: systemDefaultProvider,
            })
          }
        />
      </div>
    </>
  );
};
